import { useCallback } from 'react';

import {
  ActivityTypeGroups, isPartnerReferralActivity, isPatientAssessmentFormActivity,
  isPatientMatchActivity,
  isPatientPsychiatryActivity
} from '@uplift/api';
import { usePatientActivityQuery } from '@uplift/queries';

export const useFindInitialActivity = (
  patientId: string,
  activityType: ActivityTypeGroups | undefined,
  activityId: string | undefined,
) => {
  const enabled = Boolean(activityType && activityId);

  const { data, hasNextPage, fetchNextPage, isLoading, isFetching } = usePatientActivityQuery(patientId, {}, {
    enabled
  });

  const findInitialActivity = useCallback(async () => {
    do {
      if (!enabled) {
        return null;
      }

      if (!data?.pages && isLoading) {
        return null;
      }

      const activity = data.pages.flatMap(x => x.data).find(activity => {
        if (activityType === 'assessment' && isPatientAssessmentFormActivity(activity)) {
          if (activity.submission.formSubmissionId === activityId || activity.submission.formId === activityId) {
            return activity;
          }
        } else if (activityType === 'psychiatrySession' && isPatientPsychiatryActivity(activity)) {
          if (activity.appointment.id === activityId) {
            return activity;
          }
        } else if (activityType === 'referral' && isPartnerReferralActivity(activity)) {
          if (activity.aboutUser.user.id === activityId) {
            return activity;
          }
        } else if (activityType === 'match' && isPatientMatchActivity(activity)) {
          if (activity.match.createdBy === activityId) {
            return activity;
          }
        }

        return null;
      });

      if (activity) {
        return activity;
      }

      // this shouldn't be necessary but let's be defensive
      if (!hasNextPage && !isFetching) {
        return null;
      }

      if (hasNextPage && !isFetching) {
        await fetchNextPage();
      }
    } while (hasNextPage && !isFetching);

    // nothing has been found
    return null;
  }, [ activityId, activityType, data?.pages, enabled, fetchNextPage, hasNextPage, isFetching, isLoading ]);

  return {
    findInitialActivity
  };
};
